import React, { useState, useRef } from 'react';

const requiredFields = [
  'FirstName', 'LastName', 'FullName',
  'MailingAddress', 'MailingAddress2', 'MailingCity', 'MailingState', 'MailingZip',
  'PropertyAddress', 'PropertyAddress2', 'PropertyCity', 'PropertyState', 'PropertyZip'
];
const extraFields = [
  'extra_field1','extra_field2','extra_field3','extra_field4',
  'extra_field5','extra_field6','extra_field7','extra_field8'
];
const allFields = [...requiredFields, ...extraFields];

// Create CSV template for download
const templateCsv = [
  allFields.join(','), // header
  allFields.map(f => `${f}A`).join(','), // row 1
  allFields.map(f => `${f}B`).join(','), // row 2
  allFields.map(f => `${f}C`).join(',')  // row 3
].join('\r\n');

function Deduplicator({ apiBase }) {
  const [files, setFiles] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [downloadLinks, setDownloadLinks] = useState(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  function checkRequiredFields(headersArr) {
    let missingPerFile = [];
    headersArr.forEach((headerObj) => {
      const missing = requiredFields.filter(field => !headerObj.headers.includes(field));
      if (missing.length > 0) {
        missingPerFile.push({
          filename: headerObj.filename,
          missing
        });
      }
    });
    return missingPerFile;
  }

  const handleAddFiles = (fileList) => {
    const newFiles = Array.from(fileList).filter(
      (file) =>
        !files.some(
          (existing) => existing.name === file.name && existing.size === file.size
        )
    );
    if (newFiles.length > 0) {
      setFiles((prev) => [...prev, ...newFiles]);
      setHeaders([]);
      setSelectedFields([]);
      setDownloadLinks(null);
      setError('');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files.length) {
      handleAddFiles(e.dataTransfer.files);
    }
  };

  const handleFileInputChange = (e) => {
    handleAddFiles(e.target.files);
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
    setHeaders([]);
    setSelectedFields([]);
    setDownloadLinks(null);
  };

  const fetchHeaders = async () => {
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    try {
      const res = await fetch(`${apiBase}/upload_headers`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to fetch headers');
      }
      const data = await res.json();
      const missing = checkRequiredFields(data);
      if (missing.length > 0) {
        setError(
          "Missing required columns:\n" +
          missing.map(m =>
            `${m.filename}: ${m.missing.join(', ')}`
          ).join('\n')
        );
        return;
      }
      setHeaders(data);
      setError('');
    } catch (e) {
      setError(e.message);
    }
  };

  const handleFieldChange = (field) => {
    setSelectedFields((fields) =>
      fields.includes(field)
        ? fields.filter((f) => f !== field)
        : [...fields, field]
    );
  };

  const handleProcess = async () => {
    if (selectedFields.length === 0) {
      setError('Please select at least one field to deduplicate on.');
      return;
    }
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    formData.append('dedupe_fields_json', JSON.stringify(selectedFields));
    try {
      const res = await fetch(`${apiBase}/process`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Processing failed.');
      }
      const data = await res.json();
      setDownloadLinks(data);
      setError('');
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div
      id="main-wrapper"
      onDrop={handleDrop}
      onDragOver={(e) => e.preventDefault()}
    >
      <h2>
        <a
          href="/"
          style={{
            color: "#222",
            textDecoration: "none",
            fontWeight: "bold",
            borderBottom: "2px solid #FFD600",
            transition: "border-color 0.2s",
          }}
          onMouseOver={e => e.target.style.borderBottom = "2px solid #111"}
          onMouseOut={e => e.target.style.borderBottom = "2px solid #FFD600"}
        >
          YLHQ - List Manipulator
        </a>
        &nbsp;- Deduplicator
      </h2>

      {/* CSV template download link */}
      <div style={{ marginBottom: 16, textAlign: "left" }}>
        <b>Need a CSV template?</b> &nbsp;
        <a
          href={`data:text/csv;charset=utf-8,${encodeURIComponent(templateCsv)}`}
          download="ylhq_required_fields_template.csv"
          style={{ color: "#1a0dab" }}
        >
          Download a csv file template
        </a>
      </div>

      <div className="upload-area" onClick={() => fileInputRef.current.click()}>
        Drag & drop CSV files here, or <b>click to select</b>
        <input
          type="file"
          accept=".csv"
          multiple
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileInputChange}
        />
      </div>

      {files.length > 0 && (
        <div className="uploaded-files">
          <b>Uploaded Files:</b>
          <ul>
            {files.map((file, idx) => (
              <li key={file.name + idx}>
                {file.name}
                <button
                  className="remove-btn"
                  onClick={() => removeFile(idx)}
                  type="button"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <button
        className="main-btn"
        onClick={fetchHeaders}
        disabled={files.length === 0}
      >
        Read Headers
      </button>

      {error && (
        <div className="error-message" style={{ whiteSpace: 'pre-wrap' }}>
          {error}
          {error.includes("Missing required columns") && (
            <>
              <br /><br />
              To fix, use the{' '}
              <a
                href="https://listmanipulator.yellowletterhq.com/field-mapper"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#1a0dab" }}
              >
                Field Mapper
              </a>{' '}
              to standardize your file headers!
            </>
          )}
        </div>
      )}

      {headers.length > 0 && (
        <>
          <div style={{ margin: "30px 0" }}>
            <b>Select fields to deduplicate on:</b>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 15, marginTop: 10 }}>
              {requiredFields.map(field => (
                <label key={field} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <input
                    type="checkbox"
                    checked={selectedFields.includes(field)}
                    onChange={() => handleFieldChange(field)}
                  />
                  {field}
                </label>
              ))}
              {extraFields.map(field => (
                <label key={field} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <input
                    type="checkbox"
                    checked={selectedFields.includes(field)}
                    onChange={() => handleFieldChange(field)}
                  />
                  {field}
                </label>
              ))}
            </div>
          </div>
          <button
            className="main-btn"
            onClick={handleProcess}
            style={{ marginTop: 20, display: 'block' }}
          >
            Process
          </button>
        </>
      )}

      {downloadLinks && (
        <div style={{ marginTop: 30 }}>
          <div>
            <b>Unique-only rows:</b> {downloadLinks.num_unique_only_rows}<br />
            <b>Deduplicated rows:</b> {downloadLinks.num_deduplicated_rows}<br />
            <b>Removed rows:</b> {downloadLinks.num_removed_rows}<br />
            <b>Duplicate groups:</b> {downloadLinks.num_duplicate_groups}
          </div>

          <div style={{ marginTop: 10 }}>
            <a
              href={`${apiBase.replace('/api', '')}${downloadLinks.unique_only_file}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Download Unique Rows Only
            </a>
            <br />
            <a
              href={`${apiBase.replace('/api', '')}${downloadLinks.deduplicated_file}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Download Deduplicated Rows
            </a>
            <br />
            <a
              href={`${apiBase.replace('/api', '')}${downloadLinks.duplicates_file}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Download Duplicates Groups
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

export default Deduplicator;
